<template>
  <div class="booking-customer-page">
    <div class="header-card">
      <h2>My Bookings</h2>
      <p>Active and upcoming trips for your organization.</p>
    </div>

    <div class="filters-card">
      <label>
        From
        <input type="date" v-model="filters.fromDate" />
      </label>
      <label>
        To
        <input type="date" v-model="filters.toDate" />
      </label>
      <label>
        Status
        <select v-model="filters.status">
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="upcoming">Upcoming</option>
          <option value="completed">Completed</option>
        </select>
      </label>
      <label class="search-label">
        Booking ID
        <input type="text" v-model="filters.booking_search" placeholder="Search by booking ID" />
      </label>
      <b-button variant="primary" @click="loadData">Refresh</b-button>
    </div>

    <div class="summary-row" v-if="summary">
      <span><b>{{ summary.count || 0 }}</b> booking(s)</span>
      <span>{{ filters.fromDate }} to {{ filters.toDate }}</span>
      <span>Status: {{ filters.status }}</span>
    </div>

    <div v-if="loading" class="loading-wrap">
      <b-spinner />
    </div>

    <div v-else class="booking-list-wrap">
      <div v-if="items.length === 0" class="empty-card">
        No bookings found for this date range.
      </div>
      <div v-for="row in items" :key="row.booking_id" class="booking-card">
        <div class="card-top">
          <div>
            <div class="booking-id">{{ row.booking_id }}</div>
            <div class="subtext">{{ row.org_name || 'Organization unavailable' }}</div>
          </div>
          <span class="status-pill" :class="row.status">{{ row.status }}</span>
        </div>
        <div class="row-line"><b>Contact:</b> {{ row.contact_name || 'N/A' }}</div>
        <div class="row-line"><b>Start:</b> {{ formatDateTime(row.start_time) }}</div>
        <div class="row-line"><b>Finish:</b> {{ formatDateTime(row.finish_time) }}</div>

        <div v-if="row.legs && row.legs.length > 1" class="legs-section">
          <div class="legs-header">{{ row.legs.length }} legs</div>
          <div v-for="(leg, idx) in row.legs" :key="idx" class="leg-row">
            <div class="leg-num">Leg {{ idx + 1 }}</div>
            <div class="leg-details">
              <span><b>Pickup:</b> {{ leg.pickup_location || 'N/A' }}</span>
              <span><b>Dropoff:</b> {{ leg.dropoff_location || 'N/A' }}</span>
              <span><b>Vehicle:</b> {{ leg.vehicle_id || 'TBD' }}</span>
              <span><b>Driver:</b> {{ leg.driver_name || 'TBD' }}</span>
              <span><b>Start:</b> {{ formatDateTime(leg.start_time) }}</span>
              <span><b>Finish:</b> {{ formatDateTime(leg.finish_time) }}</span>
              <span class="status-pill small-pill" :class="leg.status">{{ leg.status }}</span>
            </div>
          </div>
        </div>

        <div v-else>
          <div class="row-line"><b>Route:</b> {{ row.route_name || 'N/A' }}</div>
          <div class="row-line"><b>Pickup:</b> {{ row.pickup_location || 'N/A' }}</div>
          <div class="row-line"><b>Dropoff:</b> {{ row.dropoff_location || 'N/A' }}</div>
          <div class="row-line"><b>Vehicle:</b> {{ row.vehicle_id || 'TBD' }}</div>
          <div class="row-line"><b>Driver:</b> {{ row.driver_name || 'TBD' }}</div>
        </div>

        <b-button size="sm" variant="info" @click="openDetail(row)">View details</b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { tbn_get_booking_customer_list } from '../networking/tbn_apis'

const todayStr = () => new Date().toISOString().slice(0, 10)
const plusDays = (days) => {
  const dt = new Date()
  dt.setDate(dt.getDate() + days)
  return dt.toISOString().slice(0, 10)
}

export default {
  name: 'booking_customer_list_tbn',
  data() {
    return {
      loading: false,
      summary: null,
      items: [],
      filters: {
        fromDate: todayStr(),
        toDate: plusDays(7),
        status: 'all',
        booking_search: '',
      },
    }
  },
  computed: {
    ...mapState({
      user: (state) => state.profile.user,
    }),
  },
  async created() {
    await this.loadData()
  },
  methods: {
    formatDateTime(value) {
      if (!value) return 'N/A'
      return new Date(value).toLocaleString()
    },
    async loadData() {
      try {
        this.loading = true
        const params = { ...this.filters }
        const { value: { data: listData = {} } } = await tbn_get_booking_customer_list(this.user.tenant, params)
        this.summary = listData.summary || {}
        this.items = listData.results || []
      } catch (e) {
        this.$emit('showAlert', 'Unable to load booking customer data. Please try again.', 'danger', 5000)
      } finally {
        this.loading = false
      }
    },
    openDetail(row) {
      this.$router.push({ name: 'booking_customer_detail_tbn', params: { tbn_booking_hash: row.tbn_booking_hash } })
    },
  },
}
</script>

<style scoped>
.booking-customer-page { margin: 16px; }
.header-card, .filters-card, .empty-card { background: #f8f9fb; border: 1px solid #d9dce3; border-radius: 8px; padding: 12px; margin-bottom: 12px; }
.header-card h2 { margin: 0; }
.header-card p { margin: 4px 0 0; color: #566; }
.filters-card { display: flex; flex-wrap: wrap; align-items: end; gap: 10px; text-align: left; }
.filters-card label { display: flex; flex-direction: column; font-weight: 600; font-size: 13px; }
.filters-card input, .filters-card select { min-width: 140px; padding: 6px; }
.search-label input { min-width: 220px; }
.summary-row { display: flex; justify-content: space-between; flex-wrap: wrap; margin: 8px 0 12px; font-weight: 600; color: #2d3a4a; }
.booking-list-wrap { max-height: 70vh; overflow: auto; }
.booking-card { text-align: left; border: 1px solid #d9dce3; border-radius: 8px; padding: 10px; margin-bottom: 10px; background: white; }
.card-top { display: flex; justify-content: space-between; margin-bottom: 8px; }
.booking-id { font-weight: 700; font-size: 16px; }
.subtext { color: #5b6a79; font-size: 12px; }
.row-line { margin-bottom: 4px; font-size: 13px; }
.status-pill { text-transform: uppercase; font-size: 10px; padding: 3px 8px; border-radius: 10px; font-weight: 700; color: white; height: fit-content; }
.status-pill.active { background: #198754; }
.status-pill.upcoming { background: #0d6efd; }
.status-pill.completed { background: #6c757d; }
.small-pill { font-size: 9px; padding: 2px 6px; }
.legs-section { margin: 8px 0; border-top: 1px solid #e9ecef; padding-top: 8px; }
.legs-header { font-weight: 700; font-size: 13px; margin-bottom: 6px; color: #2d3a4a; }
.leg-row { display: flex; gap: 8px; align-items: flex-start; margin-bottom: 6px; padding: 6px 8px; background: #f8f9fb; border-radius: 6px; }
.leg-num { font-weight: 700; font-size: 12px; color: #495057; min-width: 42px; padding-top: 2px; }
.leg-details { display: flex; flex-wrap: wrap; gap: 6px 14px; font-size: 13px; }
.loading-wrap { padding: 24px; }
</style>
